import L from 'leaflet';

import settings from '~/settings';
import { sendBuildSuccessNotification } from '~/web2ClientAPI/supply';

import { t } from './localization';

import type { IBuildings, ExtraBuildingsDataInterface, BUILDINGS } from '~/Actions/ActionSupply';
import type { ISupplyBuilding, ISupplyBuildings } from '~/store/slices/settingsSlice';

export const BUILDING_TYPES = Object.freeze({
  NAVY_YARD: 'navy_yard',
  DRY_DOCK: 'dry_dock',
  ENGINEERING: 'engineering',
  BARRACKS: 'barracks',
  MONUMENT: 'monument',
});

export const layersOrder = {
  vessels: 2000,
  ships: 1000,
  superships_home: 90,
  steel_yard: 80,
  coal_yard: 70,
  dry_dock: 60,
  university: 50,
  design_department: 40,
  shipbuilding_factory: 30,
  headquarters: 20,
  treasury: 20,
  academy: 10,
  monument: 5,
  paragon_yard: 0,
};

export const LAYER_PARAMS = {
  tms: true,
  keepBuffer: 3,
  subdomains: settings?.supply?.tilingServiceSubdomains || {},
};

export const OWN_BASE_ACTIONS: string[] = [];

export const getSoundPositions = (buildings: Nullable<ISupplyBuildings>) => {
  const soundPositions: {
    name: keyof ExtraBuildingsDataInterface;
    x: string;
    y: string;
  }[] = [];
  if (!buildings) {
    return soundPositions;
  }

  for (const key in buildings) {
    if (Object.prototype.hasOwnProperty.call(buildings, key)) {
      let coords = buildings[key as BUILDINGS]?.markerPosition;

      if (!coords) {
        coords = [-100, -100];
        console.error('didn`t get buildings coordinates');
      }

      soundPositions.push({
        name: key as BUILDINGS,
        x: coords[1].toFixed(5),
        y: coords[0].toFixed(5),
      });
    }
  }

  return soundPositions;
};

export const sendBuildingUpgradeNotification = (buildings: ISupplyBuildings, buildingId?: keyof IBuildings) => {
  const building = buildingId && buildings[buildingId];
  if (!building) {
    return;
  }

  const meta = building.levels[building.level].meta;

  sendBuildSuccessNotification({
    title: building.levels[building.level].title,
    message: meta.bonus,
    cost: meta.cost,
    level: building.level,
    maxLevel: building.maxLevel,
  });
};

export const getBuildingBonusesList = (building: ISupplyBuilding) => {
  const isSuperShipsHome = building.name === 'superships_home';
  const bonuses: string[] = [];
  let bonus = '';

  const level = building.levels[building.level];

  if (isSuperShipsHome) {
    for (const idx in building.levels) {
      if (Number(idx) <= building.level) {
        bonus = building.levels?.[idx]?.meta?.totalBonus;
        if (bonus || bonus !== '') {
          bonuses.push(bonus);
        }
      }
    }
  } else {
    bonuses.push(!level.meta.totalBonus || level.meta.totalBonus === '' ? level.meta.function : level.meta.totalBonus);
  }

  return bonuses;
};

type IBonusCategories = Array<{
  title: string;
  buildings: BUILDINGS[];
}>;

export const BONUS_CATEGORIES: IBonusCategories = [
  {
    title: t('Корабли'),
    buildings: ['dry_dock', 'shipbuilding_factory', 'superships_home'],
  },
  {
    title: t('Ресурсы'),
    buildings: ['coal_yard', 'steel_yard', 'paragon_yard'],
  },
  {
    title: t('Опыт'),
    buildings: ['university', 'design_department', 'academy'],
  },
  {
    title: t('Клан'),
    buildings: ['treasury', 'headquarters'],
  },
  {
    title: t('Достижения'),
    buildings: ['monument', 'vessels', 'ships'],
  },
];

export const setMapControls = (enable: boolean, map: L.Map) => {
  const handlers: L.Handler[] = [
    map.dragging,
    map.scrollWheelZoom,
    map.doubleClickZoom,
    map.touchZoom,
    map.boxZoom,
    map.keyboard,
  ];

  handlers.forEach((handler) => {
    if (enable) {
      handler.enable();
    } else {
      handler.disable();
    }
  });
};

const polygonCache = new Map<string, L.LatLngBounds>();

export const getCacheKey = (buildingId: BUILDINGS, level: number) => `${buildingId}_level_${level}`;

export const getPolygonBounds = (cacheKey: string, geoData: L.LatLngExpression[][]): L.LatLngBounds => {
  if (polygonCache.has(cacheKey)) {
    return polygonCache.get(cacheKey)!;
  } else {
    const polygon = L.polygon(geoData);
    const bounds = polygon.getBounds();
    polygonCache.set(cacheKey, bounds);
    return bounds;
  }
};
